import { DateTime } from "luxon";
import { Offer, Schedule } from "./type";

export type ScheduleBuffer = {
  start: number;
  end: number;
};

export type TimeRange = { start: string; end: string };

export function timeToDatetime(localDate: DateTime, time: string) {
  // Create a local time date
  let [hours, mins] = time.split(":");
  return localDate.set({
    hour: Number(hours),
    minute: Number(mins),
  });
}

export const validateLimitedDayOffer = (
  offer: Offer,
  checkOpenTime: DateTime,
  applyBuffers: boolean,
) => {
  if (
    offer.redeemable_days_and_hours == null ||
    offer.redeemable_days_and_hours.length == 0
  )
    return true;

  // If there was a check open time then use that
  const activeNow = scheduleIsActive(checkOpenTime, offer.redeemable_hours!);
  if (activeNow || applyBuffers == false) return activeNow;

  // If no check open time then use the current time, if that is false then we apply an hour offset either side
  if (
    scheduleIsActive(checkOpenTime, offer.redeemable_hours!, {
      start: 1,
      end: 1,
    })
  ) {
    return true;
  }

  // Limited offer not usable right now
  return false;
};

export function scheduleIsActive(
  localDate: DateTime,
  schedule: Schedule[],
  scheduleBuffer?: ScheduleBuffer,
) {
  const dayName =
    {
      1: "MONDAY",
      2: "TUESDAY",
      3: "WEDNESDAY",
      4: "THURSDAY",
      5: "FRIDAY",
      6: "SATURDAY",
      7: "SUNDAY",
    }[localDate.weekday] ?? "SUNDAY";

  // Is the day of the week supported by this schedule?
  const day = schedule.find((s) => s.day == dayName);
  if (day == null) return false;

  // Is the time of day supported by this schedule?
  if (day.time_ranges) {
    for (let range of day.time_ranges) {
      let isActive = timeRangeIsActive(
        localDate,
        localDate,
        range,
        scheduleBuffer,
      );
      if (isActive) {
        return true;
      }
    }
  }

  return false;
}

export function timeRangeIsActive(
  localDate: DateTime,
  rangeDate: DateTime,
  range: TimeRange,
  buffer?: ScheduleBuffer,
) {
  let { startTime, endTime } = timeRangetoDatetimes(rangeDate, range);
  // Apply scheduling buffer
  if (buffer != null) {
    startTime = startTime.minus({ hours: buffer.start ?? 0 });
    endTime = endTime.plus({ hours: buffer.end ?? 0 });
  }

  if (localDate >= startTime && localDate <= endTime) {
    return true;
  }

  return false;
}

export function timeRangetoDatetimes(localDate: DateTime, range: TimeRange) {
  // We need to check if the end time is less than the start time, that means it's overlapped into next day
  const isOverlapped =
    range.end == range.start ||
    Number(range.end.slice(0, 2)) < Number(range.start.slice(0, 2));

  // Start date is always the input date
  const startDate = localDate;

  // If the times cross into next day let's increment endDate by 1 day
  const endDate = localDate.plus({ days: isOverlapped ? 1 : 0 });

  return {
    startTime: timeToDatetime(startDate, range.start),
    endTime: timeToDatetime(endDate, range.end),
  };
}
